import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LuFolderOpen, LuImage, LuShare2, LuHeart, LuShieldCheck,
  LuLock, LuKeyRound, LuArrowRight, LuLayers, LuPaintbrush,
  LuZap, LuSmartphone
} from 'react-icons/lu';
import PublicLayout from './PublicLayout';

const showcaseItems = [
  {
    icon: LuImage,
    title: 'Your entire library, organized',
    description: 'See every photo at a glance with a fast, responsive grid. Filter by format, search by name, and batch-select for quick edits. Built for libraries of any size.',
    screenshot: '/screenshots/library-full.png',
    alt: 'Photo library grid showing hundreds of organized photos'
  },
  {
    icon: LuFolderOpen,
    title: 'Albums that work like you do',
    description: 'Group photos into albums for clients, events, or personal projects. Each album gets its own cover, share link, and access controls. Drag to reorder, tag for quick search.',
    screenshot: '/screenshots/albums-full.png',
    alt: 'Albums collection view with cover photos'
  },
  {
    icon: LuShare2,
    title: 'Share on your terms',
    description: 'Create share links with granular controls: set expirations, require passwords, enable or disable downloads, and watermark shared images. Track views and revoke access anytime.',
    screenshot: '/screenshots/shares-full.png',
    alt: 'Share management dashboard with active album shares'
  },
  {
    icon: LuHeart,
    title: 'Curate your best work',
    description: 'Mark your favorites to quickly surface your strongest images. Double-tap or click the heart, and favorited photos appear in a dedicated view ready for portfolio building or client picks.',
    screenshot: '/screenshots/favorites-full.png',
    alt: 'Favorites collection with curated photos'
  },
  {
    icon: LuPaintbrush,
    title: 'Watermark protection built in',
    description: 'Upload your logo and configure positioning, opacity, and scale. Watermarks apply automatically to shared galleries. Your originals stay untouched.',
    screenshot: '/screenshots/watermark-full.png',
    alt: 'Watermark configuration panel'
  },
];

const principleItems = [
  { icon: LuShieldCheck, title: 'We do not sell your data', description: 'No ad networks, no third-party trackers, no partners mining your content.' },
  { icon: LuLock, title: 'Private by default', description: 'Everything starts private. Sharing is something you turn on explicitly.' },
  { icon: LuKeyRound, title: 'No model training on your photos', description: 'Your uploads are never used to train AI models, now or later.' },
  { icon: LuZap, title: 'AI only with consent', description: 'If we add smart tools, they will be opt-in, transparent, and ownership-safe.' }
];

export default function DaileyCloud() {
  useEffect(() => {
    document.title = 'Features | Photo Harbor';
  }, []);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="lp-hero" style={{ paddingBottom: '2rem' }}>
        <div className="lp-hero-inner">
          <h1 className="lp-h1">
            Professional tools.<br />
            <span className="lp-h1-accent">Private by design.</span>
          </h1>
          <p className="lp-hero-sub">
            Everything you need to manage, organize, and share your photography, without giving up ownership.
          </p>
          <div className="lp-hero-actions">
            <Link to="/login?mode=signup" className="lp-btn-primary">
              Start your free trial <LuArrowRight size={16} />
            </Link>
            <Link to="/welcome" className="lp-btn-ghost">
              See pricing <LuArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Feature showcases */}
      {showcaseItems.map((item, i) => (
        <section
          key={item.title}
          className={`ft-showcase ${i % 2 === 1 ? 'ft-showcase-reverse' : ''}`}
        >
          <div className="ft-showcase-inner">
            <div className="ft-showcase-text">
              <div className="ft-showcase-icon">
                <item.icon size={22} />
              </div>
              <h2>{item.title}</h2>
              <p>{item.description}</p>
            </div>
            <div className="ft-showcase-image">
              <div className="ft-screenshot-frame">
                <img
                  src={item.screenshot}
                  alt={item.alt}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* RAW support */}
      <section className="lp-raw" style={{ marginTop: '2rem' }}>
        <div className="lp-section-inner">
          <div className="lp-raw-inner">
            <div className="lp-raw-icon"><LuLayers size={28} /></div>
            <div>
              <h3>Upload any format</h3>
              <p>Canon CR2/CR3, Nikon NEF/NRW, Sony ARW, Adobe DNG, Fujifilm RAF, Olympus ORF, Panasonic RW2, Pentax PEF, HEIC, and more. We generate optimized previews automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Multi-device callout */}
      <section className="lp-raw" style={{ marginTop: '1.5rem' }}>
        <div className="lp-section-inner">
          <div className="lp-raw-inner">
            <div className="lp-raw-icon"><LuSmartphone size={28} /></div>
            <div>
              <h3>Access from any device</h3>
              <p>Web-first with mobile-ready access. Your library follows you from desktop to phone with the same organization, favorites, and share links.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="lp-trust" style={{ marginTop: '3rem' }}>
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2>What we believe</h2>
            <p>Your photos are the product you're paying us to protect, not inventory for advertisers.</p>
          </div>
          <div className="lp-trust-grid">
            {principleItems.map((p) => (
              <div key={p.title} className="lp-trust-card">
                <div className="lp-trust-icon"><p.icon size={22} /></div>
                <h3>{p.title}</h3>
                <p>{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="lp-final-cta">
        <div className="lp-section-inner">
          <h2>Ready to take control of your library?</h2>
          <p>Start your 14-day free trial. Cancel anytime.</p>
          <Link to="/login?mode=signup" className="lp-btn-primary lp-btn-lg">
            Get started free <LuArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
